import React, { ChangeEvent, useEffect, useState } from "react";
import {
  Box,
  TextField,
  FormControl,
  Typography,
  Switch,
  FormControlLabel,
  Grid,
  Button,
  Stack,
  Checkbox,
} from "@mui/material";
import { PostgresTool } from "../../../Shared-ui/Types/Tool";
import {
  isOnlyAlphabets,
  isOnlyAlphabetsNumbers,
} from "../../../Shared-ui/utils/Common/Common";
import { useDispatch } from "react-redux";
import {
  StartLoading,
  StopLoading,
} from "../../../Shared-ui/Store/LoadingReducer/LoaderActions";
import BaseInstance from "../../../Services/AxiosInstance/BaseInstance";
import { TOOLKIT, TOOLS } from "../../../Services/Endpoint";
import { ShowError } from "../../../Shared-ui/Store/MessageReducer/MsgActions";
import { ToolsKitTypeEnum } from "../../../Shared-ui/Enum/ToolsEnum";

interface MycomponentProps {
  handleClose: () => void;
  tool: PostgresTool | null;
  // isDuplicateToolName: (name: string) => boolean;
}

const PostgresTools: React.FC<MycomponentProps> = (props) => {
  const toolErrIntial = {
    name: { error: "", value: false },
    description: { error: "", value: false },
    host: { error: "", value: false },
    user: { error: "", value: false },
    port: { error: "", value: false },
    db_name: { error: "", value: false },
    password: { error: "", value: false },
  };
  const toolInitial: PostgresTool = {
    name: "",
    description: "",
    type: "toolkit",
    is_active: true,
    config: {
      toolkit_type: ToolsKitTypeEnum.Postgres,
      host: "",
      port: 5432,
      db_name: "",
      user: "",
      password: "",
      connection: "",
      run_queries: true,
      inspect_queries: false,
      summarize_tables: true,
      export_tables: false,
    },
  };

  const [tools, setTools] = useState<PostgresTool>(toolInitial);
  const [toolsErr, setToolsErr] = useState(toolErrIntial);
  const [isEditing, setIsEditing] = useState(false);
  const dispatch = useDispatch();
  const [disableSubmit, setDisableSubmit] = useState<boolean>(true);

  useEffect(() => {
    // Check if all required agent fields are filled (returns true/false)
    const requiredFieldsFilled =
      !!tools.name.trim() &&
      !!tools.description.trim() &&
      !!tools.config.host.trim() &&
      !!tools.config.user.trim() &&
      !!tools.config.db_name.trim() &&
      !!tools.config.password.trim();

    const isPortValid = /^\d{4,5}$/.test(String(tools.config.port));

    setDisableSubmit(!(requiredFieldsFilled && isPortValid));
  }, [tools]);

  useEffect(() => {
    if (props.tool) {
      setTools(props.tool);
      setIsEditing(true);
    }
  }, []);

  const handleCreateTool = async () => {
    if (!validateTool()) return;
    try {
      dispatch(StartLoading());
      const data = await BaseInstance.post(TOOLKIT, tools).then(
        (res) => res?.data
      );
      handleCancel();
    } catch (err: any) {
      dispatch(
        ShowError(
          err?.response?.data?.detail || err?.response?.data?.statusText
        )
      );
    } finally {
      dispatch(StopLoading());
    }
  };

  const handleupdateTool = async () => {
    if (!validateTool()) return;
    try {
      dispatch(StartLoading());
      const data = await BaseInstance.put(`${TOOLS}/${tools.id}`, tools).then(
        (res) => res?.data
      );
      handleCancel();
    } catch (err: any) {
      dispatch(
        ShowError(
          err?.response?.data?.detail || err?.response?.data?.statusText
        )
      );
    } finally {
      dispatch(StopLoading());
    }
  };

  const handleCancel = () => {
    setTools(toolInitial);
    props.handleClose();
  };

  const validateTool = () => {
    var error = 0;
    const setError = (field: string, condition: boolean, message: string) => {
      if (condition) {
        setToolsErr((prev) => ({
          ...prev,
          [field]: { error: message, value: true },
        }));
        error += 1;
      } else {
        setToolsErr((prev) => ({
          ...prev,
          [field]: { error: "", value: false },
        }));
      }
    };

    setError(
      "name",
      !tools.name.trim() || !isOnlyAlphabetsNumbers(tools.name.trim()),
      "Please enter the name A-z"
    );
    setError(
      "description",
      !tools.description.trim(),
      "Please enter the description A-z"
    );
    setError(
      "db_name",
      !tools.config.db_name.trim(),
      "Please enter the Db name"
    );
    setError("host", !tools.config.host.trim(), "Please enter the host");
    setError(
      "password",
      !tools.config.password.trim(),
      "Please enter the password"
    );
    setError("user", !tools.config.user.trim(), "Please enter the user");
    setError("port", !tools.config.port, "Please enter the port");

    return error > 0 ? false : true;
  };

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setTools((preVal) => ({ ...preVal, [name]: value }));
  };

  const handleChangeConfig = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    if (name === "port") {
      if (!/^\d*$/.test(value)) return;
      setTools((prevVal) => ({
        ...prevVal,
        config: { ...prevVal.config, [name]: value === "" ? 0 : Number(value) },
      }));
    } else {
      setTools((preVal) => ({
        ...preVal,
        config: { ...preVal.config, [name]: value },
      }));
    }
  };

  const handleChangeConfigClick = (event: any) => {
    const name = event.target.name;
    const checked = event.target.checked;
    setTools((preVal) => ({
      ...preVal,
      config: { ...preVal.config, [name]: checked },
    }));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <FormControl fullWidth error={toolsErr.name.value}>
        <TextField
          label="Tool Name"
          value={tools.name}
          name="name"
          onChange={handleChange}
          variant="outlined"
          error={toolsErr.name.value}
          helperText={toolsErr.name.error}
          inputProps={{ maxLength: 40 }}
        />
      </FormControl>

      <FormControl fullWidth error={toolsErr.description.value}>
        <TextField
          label="Description"
          value={tools.description}
          name="description"
          onChange={handleChange}
          variant="outlined"
          multiline
          rows={2}
          error={toolsErr.description.value}
          helperText={toolsErr.description.error}
          inputProps={{ maxLength: 255 }}
        />
      </FormControl>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Host"
            name="host"
            value={tools.config.host}
            onChange={handleChangeConfig}
            variant="outlined"
            error={toolsErr.host.value}
            helperText={toolsErr.host.error}
            inputProps={{ maxLength: 255 }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Port"
            name="port"
            type="text"
            value={tools.config.port}
            onChange={handleChangeConfig}
            variant="outlined"
            error={toolsErr.port.value}
            helperText={toolsErr.port.error}
            inputProps={{ maxLength: 5 }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Database Name"
            name="db_name"
            value={tools.config.db_name}
            onChange={handleChangeConfig}
            variant="outlined"
            error={toolsErr.db_name.value}
            helperText={toolsErr.db_name.error}
            inputProps={{ maxLength: 40 }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Username"
            name="user"
            value={tools.config.user}
            onChange={handleChangeConfig}
            variant="outlined"
            error={toolsErr.user.value}
            helperText={toolsErr.user.error}
            inputProps={{ maxLength: 40 }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Password"
            type="password"
            name="password"
            value={tools.config.password}
            onChange={handleChangeConfig}
            variant="outlined"
            error={toolsErr.password.value}
            helperText={toolsErr.password.error}
            inputProps={{ maxLength: 40 }}
          />
        </Grid>
      </Grid>
      <Typography variant="body1" gutterBottom>
        Configuration of PostgreSQL
      </Typography>

      {/* checkbox  */}

      {/* Row 1 */}
      <Stack
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              onClick={handleChangeConfigClick}
              name="run_queries"
              checked={tools.config.run_queries}
            />
          }
          label="Run query"
        />
        <FormControlLabel
          control={
            <Checkbox
              name="inspect_queries"
              onClick={handleChangeConfigClick}
              checked={tools.config.inspect_queries}
            />
          }
          label="Inspect queries"
        />
        <FormControlLabel
          control={
            <Checkbox
              name="export_tables"
              onClick={handleChangeConfigClick}
              checked={tools.config.export_tables}
            />
          }
          label="Export tables"
        />
        <FormControlLabel
          control={
            <Checkbox
              name="summarize_tables"
              onClick={handleChangeConfigClick}
              checked={tools.config.summarize_tables}
            />
          }
          label="Summarize tables"
        />
      </Stack>

      <FormControlLabel
        control={
          <Switch
            checked={tools.is_active}
            name="is_active"
            onChange={(e) =>
              setTools((preVal) => ({
                ...preVal,
                [e.target.name]: e.target.checked,
              }))
            }
          />
        }
        label="Active"
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          mb: 3,
        }}
      >
        <Button
          variant="outlined"
          size="large"
          sx={{ textTransform: "capitalize" }}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          disabled={disableSubmit}
          size="large"
          sx={{ textTransform: "capitalize" }}
          onClick={() => (isEditing ? handleupdateTool() : handleCreateTool())}
        >
          {isEditing ? "Update" : "Create"}
        </Button>
      </Box>
    </Box>
  );
};

export default PostgresTools;
